import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Leaf, MapPin, Wrench } from "lucide-react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DashboardFilters {
  regionId: string;
  organizationId: string;
  siteId: string;
}

interface MaintenanceBike {
  id: string; // maintenance issue id
  bikeId: string;
  name: string;
  maintenanceStatus: string;
  assignedLocation: string;
}

interface MaintenanceBikesProps {
  isLoading: boolean;
  filters?: DashboardFilters;
}

const getMaintenanceBikes = async (filters?: DashboardFilters): Promise<MaintenanceBike[]> => {
  const { data, error } = await supabase
    .from('maintenance_issues')
    .select(`
      id,
      status,
      bikes (
        id,
        serial_number,
        site_id,
        sites (
          name,
          organization_id,
          organizations (
            id,
            region_id
          )
        )
      )
    `)
    .in('status', ['reported', 'in_progress'])
    .order('reported_at', { ascending: false });

  if (error) {
    console.error("Error fetching maintenance bikes:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    return [];
  }

  // Filter the data based on the provided filters
  let filteredData = data;
  
  if (filters?.siteId && filters.siteId !== 'all') {
    filteredData = filteredData.filter(issue => issue.bikes.site_id === filters.siteId);
  } else if (filters?.organizationId && filters.organizationId !== 'all') {
    filteredData = filteredData.filter(issue => 
      issue.bikes.sites?.organization_id === filters.organizationId
    );
  } else if (filters?.regionId && filters.regionId !== 'all') {
    filteredData = filteredData.filter(issue => 
      issue.bikes.sites?.organizations?.region_id === filters.regionId
    );
  }

  // Limit to 5 after filtering
  filteredData = filteredData.slice(0, 5);

  return filteredData.map((issue): MaintenanceBike => ({
    id: issue.id, // maintenance issue id
    bikeId: issue.bikes.id,
    name: `eBike #${issue.bikes.serial_number.substring(0, 6)}`,
    maintenanceStatus: issue.status,
    assignedLocation: issue.bikes.sites?.name || 'Unassigned'
  }));
};

const MaintenanceBikes: React.FC<MaintenanceBikesProps> = ({ isLoading: parentIsLoading, filters }) => {
  const { data: bikes = [], isLoading: queryIsLoading } = useQuery({
    queryKey: ['maintenanceBikes', filters],
    queryFn: () => getMaintenanceBikes(filters),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const isLoading = parentIsLoading || queryIsLoading;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle>eBikes Needing Attention</CardTitle>
          <Wrench className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-muted"></div>
            ))}
          </div>
        ) : bikes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="rounded-full bg-green-500/10 p-3">
              <Leaf className="h-6 w-6 text-green-500" />
            </div>
            <h3 className="mt-3 text-sm font-medium">All eBikes OK</h3>
            <div className="mt-1 text-sm text-muted-foreground">
              No eBikes currently need maintenance. Smashing!
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {bikes.map((bike) => (
              <Link
                key={bike.id}
                to={`/maintenance/${bike.id}`}
                className="flex items-center gap-4 rounded-lg border p-3 transition-colors hover:bg-muted"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{bike.name}</p>
                    <span className="text-xs font-medium uppercase text-amber-600">
                      {bike.maintenanceStatus === "in_progress"
                        ? "In Repair"
                        : "Needs Check"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    <span>{bike.assignedLocation}</span>
                  </div>
                </div>
              </Link>
            ))}
            <div className="pt-2 text-center">
              <Button variant="link" asChild>
                <Link to="/maintenance">View all maintenance issues</Link>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MaintenanceBikes;
